import React, { useState, useEffect } from 'react';
import { Users, Play, RefreshCw, Calendar, TrendingUp, CheckCircle, Bell, Clock, XCircle} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import API_URL from '../config/API_URL';

const Toast = ({ message, type = 'success', onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <motion.div
      initial={{ x: 400, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      exit={{ x: 400, opacity: 0 }}
      style={{
        position: 'fixed',
        top: '2rem',
        right: '2rem',
        background: type === 'success' ? 'rgba(16, 185, 129, 0.95)' : 'rgba(239, 68, 68, 0.95)',
        backdropFilter: 'blur(10px)',
        borderRadius: '12px',
        boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)',
        padding: '1rem 1.5rem',
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        zIndex: 10000,
        border: `1px solid ${type === 'success' ? '#10b981' : '#ef4444'}`,
        minWidth: '300px',
        color: 'white',
      }}
    >
      {type === 'success' ? <CheckCircle size={24} /> : <XCircle size={24} />}
      <span style={{ fontSize: '0.95rem', fontWeight: 500 }}>{message}</span>
    </motion.div>
  );
};

const StatsCard = ({ title, value, icon: Icon, color, subtitle }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      style={{
        background: 'rgba(20, 20, 35, 0.6)',
        backdropFilter: 'blur(20px)',
        borderRadius: '16px',
        padding: '1.5rem',
        border: `1px solid ${color}30`,
        boxShadow: `0 4px 30px ${color}20`,
        position: 'relative',
        overflow: 'hidden',
      }}
      whileHover={{
        scale: 1.02,
        boxShadow: `0 8px 40px ${color}30`,
      }}
    >
      <div style={{
        position: 'absolute',
        top: '-50%',
        right: '-10%',
        width: '150px',
        height: '150px',
        background: `radial-gradient(circle, ${color}15 0%, transparent 70%)`,
        borderRadius: '50%',
      }} />
      
      <div style={{ position: 'relative', zIndex: 1 }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '0.75rem',
        }}>
          <span style={{
            fontSize: '0.9rem',
            color: '#a0a0c0',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '1px',
            fontFamily: "'Rajdhani', sans-serif",
          }}>
            {title}
          </span>
          <Icon size={24} style={{ color: color }} />
        </div>
        
        <div style={{
          fontSize: '2.5rem',
          fontWeight: 900,
          fontFamily: "'Orbitron', monospace",
          background: `linear-gradient(135deg, ${color}, ${color}80)`,
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          marginBottom: '0.5rem',
        }}>
          {value}
        </div>
        
        {subtitle && (
          <div style={{
            fontSize: '0.85rem',
            color: '#a0a0c0',
            fontWeight: 500,
          }}>
            {subtitle}
          </div>
        )}
      </div>
    </motion.div>
  );
};

const ContactSchedulerAdmin = () => {
  const [stats, setStats] = useState(null);
  const [schedulers, setSchedulers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('success');

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadData = async () => {
    try {
    const [statsRes, schedulersRes] = await Promise.all([
      fetch(`${API_URL}/admin/contact-scheduler/stats`, { credentials: 'include' }), // CHANGED
      fetch(`${API_URL}/admin/contact-scheduler/schedulers`, { credentials: 'include' }) // CHANGED
    ]);

      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setStats(statsData.stats);
      }

      if (schedulersRes.ok) {
        const schedulersData = await schedulersRes.json();
        setSchedulers(schedulersData.schedulers);
      }
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTriggerCheck = async () => {
    try {
    const response = await fetch(`${API_URL}/admin/contact-scheduler/trigger-check`, { // CHANGED
      method: 'POST',
      credentials: 'include'
    });

      if (response.ok) {
        setToastMessage('Contact scheduler check triggered!');
        setToastType('success');
        setShowToast(true);
        setTimeout(loadData, 1000);
      } else {
        throw new Error('Failed to trigger check');
      }
    } catch (error) {
      setToastMessage('Failed to trigger check');
      setToastType('error');
      setShowToast(true);
    }
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return 'Never';
    const date = new Date(isoString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const styles = {
    container: {
      padding: '0',
    },
    header: {
      marginBottom: '2rem',
    },
    title: {
      fontSize: '2.5rem',
      fontWeight: 700,
      fontFamily: "'Orbitron', monospace",
      background: 'linear-gradient(135deg, #00fff5, #8b5cf6)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      marginBottom: '0.5rem',
      letterSpacing: '2px',
    },
    subtitle: {
      fontSize: '1.1rem',
      color: '#a0a0c0',
      fontFamily: "'Rajdhani', sans-serif",
      fontWeight: 500,
    },
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '1.5rem',
      marginBottom: '2rem',
    },
    controlsBar: {
      display: 'flex',
      gap: '1rem',
      marginBottom: '1.5rem',
    },
    button: {
      padding: '0.875rem 1.5rem',
      background: 'rgba(20, 20, 35, 0.6)',
      backdropFilter: 'blur(20px)',
      border: '1px solid rgba(0, 255, 245, 0.3)',
      borderRadius: '12px',
      color: '#00fff5',
      fontSize: '1rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      fontFamily: "'Rajdhani', sans-serif",
      letterSpacing: '0.5px',
      textTransform: 'uppercase',
    },
    section: {
      background: 'rgba(20, 20, 35, 0.6)',
      backdropFilter: 'blur(20px)',
      borderRadius: '16px',
      border: '1px solid rgba(0, 255, 245, 0.2)',
      boxShadow: '0 4px 30px rgba(0, 255, 245, 0.1)',
      padding: '1.5rem',
      marginBottom: '1.5rem',
    },
    sectionTitle: {
      fontSize: '1.5rem',
      fontWeight: 700,
      fontFamily: "'Orbitron', monospace",
      color: '#00fff5',
      marginBottom: '1rem',
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem',
    },
    schedulerCard: {
      background: 'rgba(30, 30, 45, 0.6)',
      borderRadius: '12px',
      padding: '1rem',
      marginBottom: '1rem',
      border: '1px solid rgba(0, 255, 245, 0.1)',
      transition: 'all 0.3s ease',
    },
    schedulerHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'start',
      marginBottom: '0.75rem',
    },
    schedulerTitle: {
      fontSize: '1.1rem',
      fontWeight: 600,
      color: '#e0e0ff',
      fontFamily: "'Rajdhani', sans-serif",
    },
    schedulerMeta: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '1rem',
      fontSize: '0.9rem',
      color: '#a0a0c0',
      fontFamily: "'Rajdhani', sans-serif",
    },
    badge: {
      display: 'inline-block',
      padding: '0.25rem 0.75rem',
      borderRadius: '6px',
      fontSize: '0.75rem',
      fontWeight: 700,
      fontFamily: "'Orbitron', monospace",
      letterSpacing: '1px',
    },
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={{ textAlign: 'center', padding: '4rem', color: '#a0a0c0' }}>
          <RefreshCw size={48} style={{ color: '#8b5cf6', marginBottom: '1rem' }} />
          <div style={{ fontSize: '1.2rem', fontFamily: "'Rajdhani', sans-serif" }}>
            Loading contact scheduler data...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <AnimatePresence>
        {showToast && (
          <Toast
            message={toastMessage}
            type={toastType}
            onClose={() => setShowToast(false)}
          />
        )}
      </AnimatePresence>

      <div style={styles.header}>
        <motion.h1
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          style={styles.title}
        >
          CONTACT SCHEDULER MANAGEMENT
        </motion.h1>
        <p style={styles.subtitle}>
          Monitor and manage contact monitoring schedules
        </p>
      </div>

      {stats && (
        <div style={styles.statsGrid}>
          <StatsCard
            title="Total Schedulers"
            value={stats.total_schedulers}
            icon={Users}
            color="#00fff5"
            subtitle={`${stats.active_schedulers} active`}
          />
          <StatsCard
            title="Total Findings"
            value={stats.total_findings}
            icon={TrendingUp}
            color="#00ff88"
            subtitle={`${stats.unread_findings} unread`}
          />
          <StatsCard
            title="Today's Findings"
            value={stats.today_findings}
            icon={Bell}
            color="#ffaa00"
            subtitle="Generated today"
          />
          <StatsCard
            title="Daily Checks"
            value={stats.daily_schedulers}
            icon={Clock}
            color="#8b5cf6"
            subtitle="Active daily"
          />
          <StatsCard
            title="Weekly Checks"
            value={stats.weekly_schedulers}
            icon={Calendar}
            color="#ff3366"
            subtitle="Active weekly"
          />
          <StatsCard
            title="Users"
            value={stats.users_with_schedulers}
            icon={Users}
            color="#00fff5"
            subtitle="Using feature"
          />
        </div>
      )}

      <div style={styles.controlsBar}>
        <motion.button
          style={styles.button}
          onClick={handleTriggerCheck}
          whileHover={{ scale: 1.05, boxShadow: '0 0 20px rgba(0, 255, 245, 0.4)' }}
          whileTap={{ scale: 0.95 }}
        >
          <Play size={18} />
          Trigger Check Now
        </motion.button>

        <motion.button
          style={styles.button}
          onClick={loadData}
          whileHover={{ scale: 1.05, boxShadow: '0 0 20px rgba(0, 255, 245, 0.4)' }}
          whileTap={{ scale: 0.95 }}
        >
          <RefreshCw size={18} />
          Refresh
        </motion.button>
      </div>

      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>
          <Users size={24} />
          Active Contact Schedulers ({schedulers.length})
        </h2>
        {schedulers.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '2rem', color: '#a0a0c0' }}>
            No contact schedulers configured yet.
          </div>
        ) : (
          schedulers.map((scheduler) => (
            <motion.div
              key={scheduler.id}
              style={styles.schedulerCard}
              whileHover={{ backgroundColor: 'rgba(40, 40, 55, 0.6)' }}
            >
              <div style={styles.schedulerHeader}>
                <div>
                  <div style={styles.schedulerTitle}>
                    {scheduler.contact_name}
                    {scheduler.contact_company && ` - ${scheduler.contact_company}`}
                  </div>
                  <div style={{ fontSize: '0.85rem', color: '#8b5cf6', marginTop: '0.25rem' }}>
                    {scheduler.user_email}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  {scheduler.unread_findings > 0 && (
                    <span style={{
                      ...styles.badge,
                      background: 'rgba(0, 255, 136, 0.2)',
                      color: '#00ff88',
                      border: '1px solid #00ff88',
                    }}>
                      {scheduler.unread_findings} NEW
                    </span>
                  )}
                  <span style={{
                    ...styles.badge,
                    background: scheduler.frequency === 'daily' ? 'rgba(0, 255, 245, 0.2)' : 
                               scheduler.frequency === 'weekly' ? 'rgba(255, 170, 0, 0.2)' :
                               'rgba(139, 92, 246, 0.2)',
                    color: scheduler.frequency === 'daily' ? '#00fff5' :
                           scheduler.frequency === 'weekly' ? '#ffaa00' :
                           '#8b5cf6',
                    border: `1px solid ${scheduler.frequency === 'daily' ? '#00fff5' :
                            scheduler.frequency === 'weekly' ? '#ffaa00' :
                            '#8b5cf6'}`,
                  }}>
                    {scheduler.frequency.toUpperCase()}
                  </span>
                  {scheduler.is_active && (
                    <span style={{
                      ...styles.badge,
                      background: 'rgba(0, 255, 136, 0.2)',
                      color: '#00ff88',
                      border: '1px solid #00ff88',
                    }}>
                      ACTIVE
                    </span>
                  )}
                </div>
              </div>
              <div style={styles.schedulerMeta}>
                <div>
                  <Clock size={16} style={{ display: 'inline', marginRight: '0.5rem' }} />
                  Last Check: {formatDateTime(scheduler.last_check)}
                </div>
                <div>
                  <Bell size={16} style={{ display: 'inline', marginRight: '0.5rem' }} />
                  {scheduler.total_findings} Total Findings
                </div>
                <div>
                  Created: {formatDateTime(scheduler.created_at)}
                </div>
              </div>
            </motion.div>
          ))
        )}
      </div>
    </div>
  );
};

export default ContactSchedulerAdmin;